import { useEffect, useRef, useState } from 'react'

import EmojiPicker, {
  Categories,
  SuggestionMode
} from 'emoji-picker-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useSendStoryMessage } from 'api/stories/useSendMessage/useSendMessage'
import { Icons } from 'helpers/icons'
import colors from 'styles/colors'
import { type StoryMessage as StoryMessageType } from 'types/api/message'
import { type StoryActionType } from 'types/types'

interface StoryMessageProps {
    storyId: string
    setAction: (action: StoryActionType) => any
    setIsPaused: (x: boolean) => any
}

export const StoryMessage = ({
    storyId,
    setAction,
    setIsPaused
}: StoryMessageProps) => {
    const { t } = useTranslation()
    const { mutate: sendStoryMessage, isLoading: sending } =
        useSendStoryMessage(storyId)

    const [toggleEmojiPicker, setToggleEmojiPicker] = useState<boolean>(false)
    const [message, setMessage] = useState<StoryMessageType>({
        message: ''
    })


    const emojiPickerRef = useRef<HTMLDivElement>(null)
    const emojiButtonRef = useRef<HTMLImageElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node

            if (
                toggleEmojiPicker &&
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(target) &&
                emojiButtonRef.current &&
                !emojiButtonRef.current.contains(target)
            ) {
                setToggleEmojiPicker(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [toggleEmojiPicker])

    const onEmojiClick = (emojiObject: { emoji: string }) => {
        const newText = `${message.message}${emojiObject.emoji}`
        setMessage(prevValue => {
            return { ...prevValue, message: newText }
        })
    }

    function clearTextArea() {
        setMessage(prevValue => {
            return { ...prevValue, message: '' }
        })
    }

    function handleSendMessage() {
        setToggleEmojiPicker(false)
        setAction?.('longPress')
        sendStoryMessage(message)
        clearTextArea()
        setIsPaused(false)
    }

    function handleTextClick() {
        document.getElementById('storyMessageInput')?.focus()
    }
    function handleTextChange(e: React.ChangeEvent<HTMLInputElement>) {
        setMessage(prevValue => {
            return { ...prevValue, message: e.target.value }
        })
    }

    return (
        <StoryMessageContainer>
            <StoryMessageField>
                <StoryMessageTextArea
                    id='storyMessageInput'
                    type='text'
                    onChange={handleTextChange}
                    value={message.message}
                    placeholder={`${t('message')}...`}
                    onClick={handleTextClick}
                />
                <StoryMessageSmiley
                    ref={emojiButtonRef}
                    src={Icons.smile}
                    alt={t('smile')}
                    onClick={() => {
                        setToggleEmojiPicker(!toggleEmojiPicker)
                    }}
                />
                {toggleEmojiPicker && (
                    <EmojiPickerContainer ref={emojiPickerRef}>
                            <EmojiPicker
                                onEmojiClick={(emojiData) => {
                                onEmojiClick({ emoji: emojiData.emoji })
                                }}
                                previewConfig={{
                                showPreview: false
                                }}
                                autoFocusSearch={false}
                                suggestedEmojisMode={SuggestionMode.RECENT}
                                lazyLoadEmojis
                                height={270} 
                                width={200} 
                                skinTonesDisabled
                                categories={[
                                {
                                    category: Categories.SUGGESTED,
                                    name: 'Recent'
                                },
                                {
                                    category: Categories.SMILEYS_PEOPLE,
                                    name: 'Smileys & People'
                                }
                                ]}
                            />
                    </EmojiPickerContainer>
                )}
            </StoryMessageField>
            <StoryMessageSendButton
                $sending={sending}
                onClick={handleSendMessage}
            >
                <StoryMessageSendButtonIcon
                    src={Icons.lightSend}
                    alt={t('send')}
                />
            </StoryMessageSendButton>
        </StoryMessageContainer>
    )
}

const StoryMessageContainer = styled.div`
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
    margin-top: 1rem;
    margin-bottom: 1rem;
`

const StoryMessageField = styled.div`
    display: flex;
    align-items: center;
    width: 80%;
    position: relative;
    flex: 1 1;
`

const StoryMessageTextArea = styled.input`
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-weight: 500;
    letter-spacing: 0.02em;
    font-size: 1.4rem;
    line-height: 1.7rem;
    background: rgba(255, 255, 255, 0.2);
    border: 0.1rem solid rgba(38, 42, 51, 0.2);
    border-radius: 2rem;
    box-sizing: border-box;
    outline: none;
    width: 100%;
    min-width: 0;
    color: #F5F5F5;
    padding-right: 6.5rem;
    padding-left: 1.6rem;
    height: 4.5rem;

    &::placeholder {
        color: #FFFFFF;
        opacity: 0.55;
    }

    &::-webkit-input-placeholder {
        color: #FFFFFF;
        opacity: 0.55;
    }

    &:-moz-placeholder {
        color: #FFFFFF;
        opacity: 0.55;
    }

    &::-moz-placeholder {
        color: #FFFFFF;
        opacity: 0.55;
    }

    &:-ms-input-placeholder {
        color: #FFFFFF;
        opacity: 0.55;
    }
`

const StoryMessageSmiley = styled.img`
    position: absolute;
    right: 2rem;
    top: 50%;
    transform: translateY(-50%);
    filter: brightness(0) invert(1);
    cursor: pointer;
`

const StoryMessageSendButton = styled.div<{ $sending: boolean }>`
    background: ${colors.blue92deg};
    border: 0.3rem solid #76bcff;
    box-sizing: border-box;
    box-shadow: 0 0.4rem 1.4rem rgba(47, 152, 254, 0.5);
    border-radius: 16rem;
    width: 4.5rem;
    height: 4.5rem;
    position: relative;
    margin-left: 1rem;
    ${props => props.$sending && 'pointer-events: none; opacity: 0.7;'}
`

const StoryMessageSendButtonIcon = styled.img`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1);
`

const EmojiPickerContainer = styled.div`
    position: absolute;
    bottom: 110%;
    right: 0;
        .epr-category-nav {
        display: none;
    }
`